/**
 * Master AI Committee section (v1.3.0, Part 1 & 3): the executive summary,
 * the committee's suggested overall action, and every documented
 * disagreement between agents — shown instead of averaged away.
 * Renders nothing for reports generated under pre-1.3 methodology.
 */

import React from "react";
import type { ResearchReport } from "@/types";

interface CommitteeMeta {
  overall_action?: string;
  technical_component_source?: string;
}

export default function CommitteeSection({
  report,
}: {
  report: ResearchReport;
}): React.ReactElement | null {
  if (!report.executive_summary) return null;

  const committee = (report.agent_results?.committee ?? {}) as CommitteeMeta;
  const disagreements = report.agent_disagreements ?? [];

  return (
    <div className="panel p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-lg font-semibold text-mist-100">Committee executive summary</h2>
        <p className="text-xs uppercase tracking-wide text-mist-400">
          Methodology {report.methodology_version}
        </p>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-mist-200">{report.executive_summary}</p>

      {committee.overall_action && (
        <p className="mt-4 rounded-lg border border-ink-700 bg-ink-800/50 p-3 text-sm text-mist-200">
          <span className="text-xs uppercase tracking-wide text-mist-400">Suggested action · </span>
          {committee.overall_action}
        </p>
      )}

      {disagreements.length > 0 && (
        <div className="mt-5">
          <h3 className="text-xs font-medium uppercase tracking-wide text-signal-amber">
            Where the agents disagree ({disagreements.length})
          </h3>
          <ul className="mt-2 space-y-3">
            {disagreements.map((disagreement, index) => (
              <li key={index} className="rounded-lg border border-ink-700 bg-ink-800/40 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium text-mist-100">{disagreement.topic}</p>
                  <p className="font-mono text-xs text-mist-400">
                    {Number(disagreement.score_a).toFixed(0)} vs {Number(disagreement.score_b).toFixed(0)}
                  </p>
                </div>
                <p className="mt-1 text-sm text-mist-300">{disagreement.explanation}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
